import React, { useState, useEffect } from 'react';
import './CashoutModal.css';
import LoadingDots from './LoadingDots';
import { useAuth } from '../contexts/AuthContext';
import { fetchCashoutOffer, acceptCashoutOffer, CashoutOffer } from '../services/api';

interface ParlayLeg {
  ingest_market_id: string;
  outcome_label: string;
  snap_implied_prob: number | null;
  ends_at: string | null;
  status: string;
  market_question: string;
  market_icon: string | null;
  event_title: string;
  event_image: string | null;
  category: string | null;
  sub_category: string | null;
}

interface ParlayTicket {
  parlay_id: string;
  total_amount: number;
  total_odds: number;
  expected_payout: number;
  status: string;
  settlement_date: string;
  created_at: string;
  actual_cashout_amount?: number;
  legs: ParlayLeg[];
}


interface CashoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: ParlayTicket;
}

const CashoutModal: React.FC<CashoutModalProps> = ({
  isOpen,
  onClose,
  ticket
}) => {
  const { user, refreshBalances } = useAuth();
  const [cashoutOffer, setCashoutOffer] = useState<CashoutOffer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch cashout offer when modal opens
  useEffect(() => {
    if (isOpen && !cashoutOffer) {
      fetchCashoutOfferData();
    }
  }, [isOpen]);

  // Clear offer when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCashoutOffer(null);
      setError(null);
    }
  }, [isOpen]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const fetchCashoutOfferData = async () => {
    if (!user?.issuer) {
      setError('User authentication required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const offer = await fetchCashoutOffer(ticket.parlay_id, user.issuer);
      setCashoutOffer(offer);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cashout offer');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptCashout = async () => {
    if (!cashoutOffer || !user?.issuer) return;

    setIsAccepting(true);
    setError(null);

    try {
      const result = await acceptCashoutOffer(cashoutOffer.offer_id, user.issuer);
      
      // Show success message and close modal
      alert(`Cashout successful! You received $${result.payout_amount.toFixed(2)}`);
      
      // Refresh balances to reflect cashout
      if (refreshBalances) {
        await refreshBalances();
      }
      
      onClose();
      
      // Refresh parent component to show updated ticket status
      window.location.reload();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process cashout');
    } finally {
      setIsAccepting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  if (!isOpen) return null;

  return (
    <div className="cashout-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="cashout-modal-content">
        <button 
          className="close-cashout-modal-btn"
          onClick={onClose}
        >
          ×
        </button>
        
        <div className="cashout-modal-header">
          <div className="cashout-modal-title">
            <div className="cashout-modal-question">Cash Out Early</div>
            <div className="cashout-modal-selection">
              Exit your {ticket.legs.length}-leg parlay before all legs settle
            </div>
          </div>
        </div>

        {isLoading && (
          <div className="cashout-loading">
            <LoadingDots className="modal-loading" />
            <p>Calculating your cashout offer...</p>
          </div>
        )}

        {error && (
          <div className="cashout-error">
            <p>{error}</p>
            <button className="retry-button" onClick={fetchCashoutOfferData}>
              Retry
            </button>
          </div>
        )}

        {cashoutOffer && (
          <>
            <div className="cashout-offer-section">
              <div className="cashout-amount">
                <div className="cashout-amount-value">
                  {formatCurrency(cashoutOffer.cashout_amount)}
                </div>
                <div className="cashout-amount-label">Cash Out Offer</div>
              </div>
              
              <div className="cashout-comparison">
                <div className="comparison-item">
                  <span className="comparison-label">Original Expected Payout:</span>
                  <span className="comparison-value original-payout">
                    {formatCurrency(ticket.expected_payout)}
                  </span>
                </div>
                <div className="comparison-item">
                  <span className="comparison-label">Early Exit Discount:</span>
                  <span className="comparison-value discount">
                    -{formatPercentage(cashoutOffer.cashout_vig)}
                  </span>
                </div>
              </div>
            </div>

            <div className="cashout-parlay-info">
              <div className="parlay-info-item">
                <span className="parlay-label">Original Stake:</span>
                <span className="parlay-value">
                  {formatCurrency(ticket.total_amount)}
                </span>
              </div>
              <div className="parlay-info-item">
                <span className="parlay-label">Original Odds:</span>
                <span className="parlay-value">
                  {ticket.total_odds.toFixed(2)}x
                </span>
              </div>
              <div className="parlay-info-item">
                <span className="parlay-label">Legs Status:</span>
                <span className="parlay-value">
                  {ticket.legs.filter(leg => leg.status === 'won').length} won, {' '}
                  {ticket.legs.filter(leg => leg.status === 'pending').length} pending
                </span>
              </div>
            </div>

            <div className="cashout-actions">
              <button 
                className="deny-cashout-btn"
                onClick={onClose}
                disabled={isAccepting}
              >
                Deny
              </button>
              <button 
                className="accept-cashout-btn"
                onClick={handleAcceptCashout}
                disabled={isAccepting}
              >
                {isAccepting ? <LoadingDots className="button-loading" /> : 'Accept'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CashoutModal;